import { useState, useEffect, useRef } from 'react'
import { UtilPageNavigation, PrimaryButton, SecondaryButton, HouseholdOnboardingForm, CustomCard, UtilRoomieBreakdownFrame, UtilRoommatesFrame, UtilReceiptFrame } from '../../components'
import {
    Box,
    Container,
    Typography,
    ThemeProvider,
    Grid,
    Fab
} from '@mui/material'
import {
    People as PeopleIcon,
    Visibility as VisibilityIcon,
    PersonAdd as PersonAddIcon
} from '@mui/icons-material'
import theme from '../../styles/theme'
import roomatesSplitingUtilsImage from '../../assets/roomates_spliting_utils.png'

export default function UtilMarketing() {
    const [showHouseholdForm, setShowHouseholdForm] = useState(false)
    const [showFloatingCTA, setShowFloatingCTA] = useState(false)
    const [showSection3, setShowSection3] = useState(false)
    const [showSection4, setShowSection4] = useState(false)
    const [showSection5, setShowSection5] = useState(false)
    const firstCTARef = useRef<HTMLDivElement>(null)
    const section3Ref = useRef<HTMLDivElement>(null)
    const section4Ref = useRef<HTMLDivElement>(null)
    const section5Ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setShowFloatingCTA(!entry.isIntersecting && !showHouseholdForm)
            },
            {
                threshold: 0.1,
                rootMargin: '-50px 0px 0px 0px'
            }
        )

        if (firstCTARef.current) {
            observer.observe(firstCTARef.current)
        }

        return () => {
            if (firstCTARef.current) {
                observer.unobserve(firstCTARef.current)
            }
        }
    }, [showHouseholdForm])

    useEffect(() => {
        const sectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const id = entry.target.getAttribute('data-section')
                        if (id === '3') setShowSection3(true)
                        if (id === '4') setShowSection4(true)
                        if (id === '5') setShowSection5(true)
                    }
                })
            },
            {
                threshold: 0.15,
                rootMargin: '-50px 0px 0px 0px'
            }
        )

        if (section3Ref.current) sectionObserver.observe(section3Ref.current)
        if (section4Ref.current) sectionObserver.observe(section4Ref.current)
        if (section5Ref.current) sectionObserver.observe(section5Ref.current)

        return () => sectionObserver.disconnect()
    }, [])

    const handleGetStarted = () => {
        setShowHouseholdForm(true)
        setShowFloatingCTA(false)
        setTimeout(() => {
            document.getElementById('household-onboarding-form')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
        }, 100)
    }

    return (
        <ThemeProvider theme={theme}>
            <UtilPageNavigation loginUrl="/util/login" />

            {/* Section 1: Hero (value prop) */}
            <Box sx={{
                bgcolor: 'white',
                minHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                paddingTop: { xs: 5, sm: 10 },
                paddingBottom: { xs: 0, sm: 0 }
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '1.875rem', sm: '2.25rem', md: '3rem' },
                                        lineHeight: 1.2,
                                        color: 'grey.900',
                                        mb: 1.5
                                    }}
                                >
                                    Split utilities. {' '}
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            position: 'relative',
                                            textAlign: 'left',
                                            paddingBottom: 1
                                        }}
                                    >
                                        Automatically
                                        <svg
                                            width="100%"
                                            height="8"
                                            viewBox="0 0 100 8"
                                            style={{
                                                position: 'absolute',
                                                bottom: '2px',
                                                left: '0',
                                                width: '100%',
                                                height: '8px'
                                            }}
                                        >
                                            <path
                                                d="M 0 6 Q 50 0 100 6"
                                                stroke="#fbbf24"
                                                strokeWidth="4"
                                                fill="none"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </Box>
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                                        color: 'grey.700',
                                        mb: 3,
                                        textAlign: 'left'
                                    }}
                                >
                                    Forward the utility bill. We calculate, notify roommates, track payments - so you don’t have to.
                                </Typography>

                                <Box ref={firstCTARef} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-start' } }}>
                                    <PrimaryButton
                                        variant="contained"
                                        size="large"
                                        onClick={handleGetStarted}
                                        sx={{
                                            bgcolor: 'grey.900',
                                            color: 'white',
                                            px: 4,
                                            py: 1.5,
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            width: 'auto',
                                            alignSelf: 'flex-start',
                                            '&:hover': {
                                                bgcolor: 'grey.800',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        Get Started ✨
                                    </PrimaryButton>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontStyle: 'italic',
                                            color: 'grey.600',
                                            textAlign: 'left',
                                            marginLeft: '12px',
                                            mt: 1
                                        }}
                                    >
                                        No credit card required
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <Box
                                    component="img"
                                    src={roomatesSplitingUtilsImage}
                                    alt="Easy, fast utility splitting for roommates"
                                    sx={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        maxHeight: { xs: '300px', sm: '400px', md: '600px' },
                                        borderRadius: '120px',
                                        width: 'auto'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Section 2: Utility splitting on autopilot (black bar) */}
            <Box sx={{
                bgcolor: 'black',
                py: { xs: 6, md: 8 },
                textAlign: 'center',
                position: 'relative',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '60px',
                    background: 'linear-gradient(to bottom, transparent, rgba(249, 250, 251, 0.1))',
                    pointerEvents: 'none'
                }
            }}>
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                            color: 'white',
                            mb: { xs: 2, md: 2.5 },
                            letterSpacing: { xs: '0.2px', sm: '0.25px', md: '0.3px' }
                        }}
                    >
                        Utility splitting on autopilot.
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem' },
                            color: 'grey.300',
                            mb: { xs: 2, md: 2.25 }
                        }}
                    >
                        Settle up without the{' '}
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-block',
                                position: 'relative',
                                paddingBottom: 1
                            }}
                        >
                            hassle
                            <svg
                                width="100%"
                                height="8"
                                viewBox="0 0 100 8"
                                style={{
                                    position: 'absolute',
                                    bottom: '2px',
                                    left: '0',
                                    width: '100%',
                                    height: '8px'
                                }}
                            >
                                <path
                                    d="M 0 6 Q 50 0 100 6"
                                    stroke="#fbbf24"
                                    strokeWidth="4"
                                    fill="none"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </Box>
                        .
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.875rem',
                            color: 'white',
                            mt: { xs: 3.75, md: 4 }
                        }}
                    >
                        Let us do it, fairly and transparently.
                    </Typography>
                </Container>
            </Box>

            {/* Section 3: Roommate Breakdown */}
            <Box
                ref={section3Ref}
                data-section="3"
                sx={{
                    py: { xs: 3, md: 4 },
                    bgcolor: 'white',
                    opacity: showSection3 ? 1 : 0,
                    transform: showSection3 ? 'translateY(0)' : 'translateY(40px)',
                    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 1 } }}>
                            <Box sx={{
                                maxWidth: 300,
                                mx: 'auto',
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                                <UtilRoomieBreakdownFrame />
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 2 } }}>
                            <Box sx={{ maxWidth: 500 }}>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.875rem', sm: '2.25rem' },
                                        color: 'grey.900',
                                        mb: 2
                                    }}
                                >
                                    See Who Owes What
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'grey.600',
                                        mb: 3,
                                        fontWeight: 400,
                                        fontSize: '1.125rem'
                                    }}
                                >
                                    Every bill is split fairly across roommates. See each person's share and payment status at a glance.
                                </Typography>

                                <Box sx={{ mb: 4 }}>
                                    <Feature
                                        title="Fair and accurate splits"
                                        icon={<PeopleIcon sx={{ color: '#fbbf24' }} />}
                                    >
                                        Each roommate sees their share, payment status, and the total — no spreadsheets needed.
                                    </Feature>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Section 4: Manage Roommates */}
            <Box
                ref={section4Ref}
                data-section="4"
                sx={{
                    py: { xs: 4, md: 5 },
                    bgcolor: 'grey.50',
                    opacity: showSection4 ? 1 : 0,
                    transform: showSection4 ? 'translateY(0)' : 'translateY(40px)',
                    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
                            <Box sx={{ maxWidth: 500 }}>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.875rem', sm: '2.25rem' },
                                        color: 'grey.900',
                                        mb: 2
                                    }}
                                >
                                    Manage Roommates with Ease
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'grey.600',
                                        mb: 3,
                                        fontWeight: 400,
                                        fontSize: '1.125rem'
                                    }}
                                >
                                    Add or remove roommates anytime. Bills adjust automatically when your household changes.
                                </Typography>

                                <Box sx={{ mb: 4 }}>
                                    <Feature
                                        title="Roommate changes, handled"
                                        icon={<PersonAddIcon sx={{ color: '#fbbf24' }} />}
                                    >
                                        Someone moving in or out? Update your household in seconds and future bills split accordingly.
                                    </Feature>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                            <Box sx={{
                                maxWidth: 300,
                                mx: 'auto',
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                                <UtilRoommatesFrame />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Section 5: Transparency / Receipt Preview */}
            <Box
                ref={section5Ref}
                data-section="5"
                sx={{
                    py: { xs: 3, md: 4 },
                    bgcolor: 'white',
                    opacity: showSection5 ? 1 : 0,
                    transform: showSection5 ? 'translateY(0)' : 'translateY(40px)',
                    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 1 } }}>
                            <Box sx={{
                                maxWidth: 300,
                                mx: 'auto',
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                                <UtilReceiptFrame />
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 2 } }}>
                            <Box sx={{ maxWidth: 500 }}>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.875rem', sm: '2.25rem' },
                                        color: 'grey.900',
                                        mb: 2
                                    }}
                                >
                                    Full{' '}
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            position: 'relative',
                                            paddingBottom: 1
                                        }}
                                    >
                                        Transparency
                                        <svg
                                            width="100%"
                                            height="8"
                                            viewBox="0 0 100 8"
                                            style={{
                                                position: 'absolute',
                                                bottom: '2px',
                                                left: '0',
                                                width: '100%',
                                                height: '8px'
                                            }}
                                        >
                                            <path
                                                d="M 0 6 Q 50 0 100 6"
                                                stroke="#fbbf24"
                                                strokeWidth="4"
                                                fill="none"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </Box>
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'grey.600',
                                        mb: 3,
                                        fontWeight: 400,
                                        fontSize: '1.125rem'
                                    }}
                                >
                                    Every roommate can view the actual bill. No guesswork, no trust issues — just the real receipt.
                                </Typography>

                                <Box sx={{ mb: 4 }}>
                                    <Feature
                                        title="View the original bill"
                                        icon={<VisibilityIcon sx={{ color: '#fbbf24' }} />}
                                    >
                                        Anyone in the household can view the receipt anytime. Full trust, zero drama.
                                    </Feature>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Renters & Landlords Promo Section */}
            <Box sx={{
                bgcolor: 'black',
                py: { xs: 5, md: 6 },
                textAlign: 'center'
            }}>
                <Container maxWidth="md">
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                            color: 'white',
                            mb: 1.5
                        }}
                    >
                        We do more than utilities.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: { xs: '1rem', sm: '1.125rem' },
                            color: 'grey.400',
                            mb: 4,
                            maxWidth: 520,
                            mx: 'auto'
                        }}
                    >
                        Whether you're hunting for a place or filling a vacancy — we've got you covered.
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'center',
                        gap: 2
                    }}>
                        <SecondaryButton
                            size="large"
                            onClick={() => window.location.href = '/renters'}
                            sx={{
                                borderColor: 'grey.600',
                                color: 'white',
                                px: 4,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'transparent',
                                    color: 'white'
                                }
                            }}
                        >
                            I'm a Renter
                        </SecondaryButton>
                        <SecondaryButton
                            size="large"
                            onClick={() => window.location.href = '/landlord'}
                            sx={{
                                borderColor: 'grey.600',
                                color: 'white',
                                px: 4,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'transparent',
                                    color: 'white'
                                }
                            }}
                        >
                            I'm a Landlord
                        </SecondaryButton>
                    </Box>
                </Container>
            </Box>

            <HouseholdOnboardingForm
                isVisible={showHouseholdForm}
                id="household-onboarding-form"
            />

            {/* Footer */}
            <Box
                component="footer"
                sx={{
                    bgcolor: 'grey.50',
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                    py: 4,
                    mt: 8
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'grey.600',
                                fontSize: '0.875rem'
                            }}
                        >
                            © {new Date().getFullYear()} HeyyProperty Inc. All rights reserved.
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'grey.500',
                                fontSize: '0.75rem'
                            }}
                        >
                            Made with ❤️
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* Floating CTA Button */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 1000,
                    opacity: showFloatingCTA ? 1 : 0,
                    transform: showFloatingCTA ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                    pointerEvents: showFloatingCTA ? 'auto' : 'none'
                }}
            >
                <Fab
                    variant="extended"
                    color="primary"
                    onClick={handleGetStarted}
                    sx={{
                        bgcolor: 'grey.900',
                        borderColor: 'white',
                        border: '1px solid',
                        color: 'white',
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                            bgcolor: 'grey.800',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                        }
                    }}
                >
                    Get Started ✨
                </Fab>
            </Box>
        </ThemeProvider>
    )
}

interface FeatureProps {
    title: string
    children: React.ReactNode
    icon?: React.ReactNode
}

function Feature({ title, children, icon }: FeatureProps) {
    return (
        <CustomCard
            sx={{
                p: 1.75,
                height: '100%'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                {icon && (
                    <Box sx={{ color: 'primary.main', mt: 0.25 }}>
                        {icon}
                    </Box>
                )}
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 600,
                        color: 'grey.900',
                        fontSize: '0.875rem'
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Typography
                variant="body2"
                sx={{
                    color: 'grey.600',
                    fontSize: '0.875rem',
                    lineHeight: 1.5
                }}
            >
                {children}
            </Typography>
        </CustomCard>
    )
}
